import type { FactorMealSummary } from '@web-app/types/session-prep';
import {
  addWeeks,
  format,
  isAfter,
  isValid,
  isWithinInterval,
  parse,
  startOfDay,
} from 'date-fns';

type FactorDateRange = FactorMealSummary['dateRange'];

/**
 * Factor order data is reported with a lag, so the coverage window's end can sit
 * a few days behind today even for an actively-ordering patient. We extend the
 * window end by this grace period before deciding the summary is stale.
 */
export const COVERAGE_END_OFFSET_WEEKS = 4;

const KNOWN_PLAN_LABELS: Record<string, string> = {
  caloriesmart: 'Calorie Smart',
  protein_plus: 'Protein Plus',
  keto: 'Keto',
  vegan: 'Vegan',
  vegetarian: 'Vegetarian',
};

/**
 * Maps known Factor plan slugs to human-readable labels, falling back to a
 * title-cased version of the slug for unknown values.
 */
export function formatPlanType(planType: string | null): string | null {
  if (!planType) return null;

  return (
    KNOWN_PLAN_LABELS[planType.toLowerCase()] ??
    planType
      .replace(/[_-]+/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase())
  );
}

/**
 * Strictly parses a `yyyy-MM-dd` string, returning null for missing or
 * unparseable values. The date range comes from the AI workflow, so a
 * malformed value must be rejected rather than silently defaulting to a
 * fabricated (e.g. current) date.
 */
function parseIsoDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const parsed = parse(value, 'yyyy-MM-dd', new Date());
  return isValid(parsed) ? parsed : null;
}

/** Formats a yyyy-MM-dd range into a readable label, e.g. "May 25 – Jun 2". */
export function formatDateRange(dateRange: FactorDateRange): string | null {
  const start = parseIsoDate(dateRange?.start);
  const end = parseIsoDate(dateRange?.end);
  if (!start || !end) return null;

  return `${format(start, 'MMM d')} – ${format(end, 'MMM d')}`;
}

/**
 * The summary is only relevant when its coverage window includes today — i.e.
 * the patient is actively ordering. The window end is extended by
 * {@link COVERAGE_END_OFFSET_WEEKS} to tolerate Factor's reporting lag. A range
 * entirely in the past (e.g. a patient who churned months ago) is stale and the
 * card is hidden. A missing or unparseable range can't be confirmed as current,
 * so it is also treated as not relevant.
 */
export function isCoverageCurrent(dateRange: FactorDateRange): boolean {
  const start = parseIsoDate(dateRange?.start);
  const end = parseIsoDate(dateRange?.end);
  if (!start || !end) return false;

  const windowStart = startOfDay(start);
  const windowEnd = addWeeks(startOfDay(end), COVERAGE_END_OFFSET_WEEKS);
  // Guard against a malformed range (start after the offset end), which would
  // otherwise make isWithinInterval throw.
  if (isAfter(windowStart, windowEnd)) return false;

  return isWithinInterval(startOfDay(new Date()), {
    start: windowStart,
    end: windowEnd,
  });
}
